import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as ReorderIcon } from 'assets/icons/reorder.svg';
import Button from 'atoms/Button';
import Container from 'atoms/Container';
import Dropdown from 'atoms/Dropdown';
import { colors } from 'constants/colors';
import {
    CSSProperties,
    FC,
    forwardRef,
    HTMLAttributes,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store';
import {
    LayoutState,
    selectLayout,
    setAddingProjectBottom,
    setAddingProjectTop,
    setProjectsReorder,
} from 'store/layoutSlice';
import {
    addProject,
    archiveProject,
    deleteProject,
    moveProject,
} from 'store/projectsThunks';
import { selectWorkspaceId } from 'store/userSlice';
import {
    selectFilteredProjects,
    selectWorkspaceData,
    WorkspaceState,
} from 'store/workspaceSlice';

import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    rectSortingStrategy,
    SortableContext,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ListHeader from 'layout/atoms/ListHeader/ListHeader';
import TextEditable from 'layout/atoms/TextEditable';
import ViewToggler from 'layout/atoms/ViewToggler';
import Project from 'layout/molecules/Project/Project';
import styles from './ProjectsList.module.scss';

const SortableItem: FC<ItemProps> = (props) => {
    const {
        isDragging,
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || undefined,
    };

    return (
        <Item
            ref={setNodeRef}
            style={style}
            withOpacity={isDragging}
            {...props}
            {...attributes}
            {...listeners}
        />
    );
};

export type ItemProps = HTMLAttributes<HTMLDivElement> & {
    id: string;
    withOpacity?: boolean;
    isDragging?: boolean;
};

const Item = forwardRef<HTMLDivElement, ItemProps>(
    ({ id, withOpacity, isDragging, style, ...props }, ref) => {
        const dispatch = useDispatch<AppDispatch>();

        const handleArchiveProject = (projectId: string) => {
            dispatch(archiveProject(projectId));
        };

        const handleDeleteProject = (projectId: string) => {
            dispatch(deleteProject(projectId));
        };

        const inlineStyles: CSSProperties = {
            opacity: withOpacity ? '0.5' : '1',
            transformOrigin: '50% 50%',
            cursor: isDragging ? 'grabbing' : 'grab',
            ...style,
        };

        return (
            <div ref={ref} style={inlineStyles} {...props}>
                <Project
                    key={id}
                    id={id}
                    folded={true}
                    onProjectDelete={() => handleDeleteProject(id)}
                    onProjectArchive={() => handleArchiveProject(id)}
                />
            </div>
        );
    }
);

const ProjectsList = () => {
    const dispatch = useDispatch<AppDispatch>();
    const layoutState: LayoutState = useSelector(selectLayout);
    const workspaceData: WorkspaceState = useSelector(selectWorkspaceData);
    const workspaceId = useSelector(selectWorkspaceId);
    const activeProjects = useSelector(selectFilteredProjects);
    const [deletedProjects, setDeletedProjects] = useState<Set<string>>(
        new Set()
    );
    const [newProjectName, setNewProjectName] = useState('');

    const getRandomNonTakenColor = (takenColors: string[]): string => {
        const availableColors = colors.filter(
            (color) => !takenColors.includes(color)
        );

        if (availableColors.length === 0) {
            const randomRed = Math.floor(Math.random() * 256);
            const randomGreen = Math.floor(Math.random() * 256);
            const randomBlue = Math.floor(Math.random() * 256);
            return `rgb(${randomRed}, ${randomGreen}, ${randomBlue})`;
        }

        const randomIndex = Math.floor(Math.random() * availableColors.length);
        return availableColors[randomIndex];
    };

    const handleAddProject = () => {
        dispatch(setAddingProjectTop(true));
        setNewProjectName('');
    };

    const handleAddProjectBottom = () => {
        dispatch(setAddingProjectBottom(true));
        setNewProjectName('');
    };

    const handleConfirmAddProject = () => {
        if (newProjectName && newProjectName.trim()) {
            const takenColors = workspaceData.projects
                .filter((p) => p.color)
                .map((p) => p.color) as string[];

            dispatch(
                addProject({
                    workspaceId: workspaceId || '',
                    name: newProjectName.trim(),
                    color: getRandomNonTakenColor(takenColors),
                    asFirst: layoutState.projectAddingTop,
                })
            );
            dispatch(setAddingProjectTop(false));
            dispatch(setAddingProjectBottom(false));
            setNewProjectName('');
        }
    };

    const handleCancelAddProject = () => {
        dispatch(setAddingProjectTop(false));
        dispatch(setAddingProjectBottom(false));
        setNewProjectName('');
    };

    const handleProjectNameChange = (event: any) => {
        setNewProjectName(event.target.value);
    };

    const handleProjectNameKeyDown = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            event.preventDefault();
            handleConfirmAddProject();
        } else if (event.key === 'Escape') {
            handleCancelAddProject();
        }
    };

    const handleMoveProject = useCallback(
        async (projectId: string, itemIndex: number, newIndex: number) => {
            return dispatch(
                moveProject({
                    workspaceId: workspaceData.id,
                    projectId,
                    itemIndex,
                    newIndex,
                })
            );
        },
        [dispatch, workspaceData]
    );

    const [items, setItems] = useState<string[]>(
        activeProjects.map((project: any) => project.id)
    );
    const [activeId, setActiveId] = useState<string | null>(null);
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

    // Update items when activeProjects changes (e.g., when a new project is added)
    useEffect(() => {
        setItems(activeProjects.map((project: any) => project.id));
    }, [activeProjects]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setTimeout(() => setActiveId(event.active.id.toString()), 100);
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (active.id !== over?.id) {
                setItems((items) => {
                    const oldIndex = items.indexOf(active.id.toString());
                    const newIndex = items.indexOf(over!.id.toString());
                    handleMoveProject(active.id.toString(), oldIndex, newIndex);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }

            setActiveId(null);
        },
        [handleMoveProject]
    );

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
    }, []);

    const handleArchiveProject = (projectId: string) => {
        dispatch(archiveProject(projectId));
    };

    const handleDeleteProject = (projectId: string) => {
        setDeletedProjects((prev) => new Set([...Array.from(prev), projectId]));
        dispatch(deleteProject(projectId)).catch(() => {
            setDeletedProjects((prev) => {
                const updated = new Set(prev);
                updated.delete(projectId);
                return updated;
            });
        });
    };

    return (
        <Container
            size={layoutState.projectsView === 'list' ? 's' : 'xl'}
            gap="16"
            marginTop="16"
        >
            <ListHeader text="Projects">
                {layoutState.projectsReorder ? (
                    <Button
                        size="m"
                        onClick={() => dispatch(setProjectsReorder(false))}
                    >
                        <ReorderIcon /> <span>Finish</span>
                    </Button>
                ) : (
                    <Dropdown position="bottomRight" horizontal={true}>
                        <Button
                            size="m"
                            align="left"
                            onClick={handleAddProject}
                        >
                            <PlusIcon />
                            <span>Add project</span>
                        </Button>
                        {activeProjects.length > 0 && (
                            <>
                                <Button
                                    size="m"
                                    align="left"
                                    onClick={() =>
                                        dispatch(setProjectsReorder(true))
                                    }
                                >
                                    <ReorderIcon /> <span>Reorder</span>
                                </Button>
                                <ViewToggler />
                            </>
                        )}
                    </Dropdown>
                )}
            </ListHeader>
            {layoutState.projectsReorder ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <SortableContext
                        items={items}
                        strategy={rectSortingStrategy}
                    >
                        <div
                            className={`${styles.wrap} ${
                                layoutState.projectsView === 'list'
                                    ? styles.list
                                    : styles.grid
                            }`}
                        >
                            {items.map((projectId) => (
                                <SortableItem key={projectId} id={projectId} />
                            ))}
                        </div>
                    </SortableContext>
                    <DragOverlay
                        adjustScale
                        style={{ transformOrigin: '0 0 ' }}
                    >
                        {activeId ? <Item id={activeId} isDragging /> : null}
                    </DragOverlay>
                </DndContext>
            ) : (
                <div
                    className={`${styles.wrap} ${
                        layoutState.projectsView === 'list'
                            ? styles.list
                            : styles.grid
                    }`}
                >
                    {layoutState.projectAddingTop && (
                        <div
                            style={{
                                border: 'none',
                                borderRadius: 'var(--radius)',
                                padding: '16px',
                                marginBottom: '16px',
                                backgroundColor: 'var(--box-background)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <TextEditable
                                value={newProjectName}
                                handleChange={handleProjectNameChange}
                                handleKeyDown={handleProjectNameKeyDown}
                                handleBlur={handleCancelAddProject}
                                maxLength={100}
                                placeholder="Enter project name"
                                onRenameConfirmedButtonClicked={
                                    handleConfirmAddProject
                                }
                            />
                        </div>
                    )}
                    {activeProjects
                        .filter((project) => !deletedProjects.has(project.id))
                        .map((project: any) => (
                            <Project
                                key={project.id}
                                id={project.id}
                                onProjectDelete={() =>
                                    handleDeleteProject(project.id)
                                }
                                onProjectArchive={() =>
                                    handleArchiveProject(project.id)
                                }
                            />
                        ))}
                    {layoutState.projectAddingBottom && (
                        <div
                            style={{
                                border: 'none',
                                borderRadius: 'var(--radius)',
                                padding: '16px',
                                marginTop: '16px',
                                backgroundColor: 'var(--box-background)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <TextEditable
                                value={newProjectName}
                                handleChange={handleProjectNameChange}
                                handleKeyDown={handleProjectNameKeyDown}
                                handleBlur={handleCancelAddProject}
                                maxLength={100}
                                placeholder="Enter project name"
                                onRenameConfirmedButtonClicked={
                                    handleConfirmAddProject
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </Container>
    );
};

export default ProjectsList;
